import { apiRequest } from '../api';

// ── Payroll › Payroll settings (batch 10 R5 — PAY-14) ────────────────────────────────────────────────
// The shapes server/src/routes/payrollSettings.ts answers, field for field (design §2.4, the R5 brief §4.3).
// Every path carries ?clientId= — the workspace gate reads it there and nowhere else — and the whole module
// sits behind O2 like the rest of /api/payroll (403 pay_forbidden); a books login is never offered the screen.
// ⛔ The server decides. Which account may answer a role, which items are answered, what is locked, who may
// edit the firm default, what a new account needs — every one of those arrives in a body below; the screen
// only draws it. The few lists mirrored here are the ones the screen needs BEFORE it has asked anything (the
// pay-day choices, which choice list feeds which item account, the reserved item codes), and
// scripts/testPayrollSettingsI18n.ts asserts each equals the server's own export, the way testPayrollI18n.ts
// holds api/payroll.ts PR_IMPORT_* to services/employeeImport.ts.

/** The five core roles (services/payrollAccounts.ts PAYROLL_ROLES), in the order the screen lists them. */
export const PRS_ROLES = ['salary_expense', 'employer_sso_expense', 'salary_payable', 'pnd1_payable', 'sso_payable'] as const;
export type PrsRole = typeof PRS_ROLES[number];
/** Why an account may not answer (payrollAccounts.ts ACCOUNT_RULES), in the order the server tests them. */
export const PRS_ACCOUNT_RULES = ['not_found', 'other_client', 'deleted', 'inactive', 'non_postable', 'control', 'cash', 'class'] as const;
export type PrsRule = typeof PRS_ACCOUNT_RULES[number];
/** The ten firm-default items the seed writes (services/payrollSettings.ts SEED_ITEM_CODES). A custom item may
 *  never take one of these codes — the server refuses it (409), so the window never sends it. */
export const PRS_SEED_ITEM_CODES = ['BASE', 'OT', 'BONUS', 'ALLOWANCE', 'OTHER_EARN', 'ABSENCE', 'WELFARE_EE', 'OTHER_DED', 'WELFARE_ER', 'OTHER_ER'] as const;
export const PRS_ITEM_KINDS = ['earning', 'reduction', 'deduction', 'employer'] as const;
export type PrsKind = typeof PRS_ITEM_KINDS[number];
/** payrollSettings.ts SUMMARY_GROUPS — the month summary's lines each kind may land on. */
export const PRS_SUMMARY_GROUPS: Record<PrsKind, readonly string[]> = {
  earning: ['basic', 'overtime', 'bonus_allowance', 'other_earning'],
  reduction: ['absence'],
  deduction: ['welfare_ee', 'other_deduction'],
  employer: ['welfare_er', 'other_employer'],
};
/** The three yes / no / not-answered questions an earning or a reduction carries (payrollSettings.ts ANSWER_KEYS). */
export const PRS_ANSWER_KEYS: Record<PrsKind, readonly PrsAnswerKey[]> = {
  earning: ['pnd1_taxable', 'sso_base', 'pnd1_regular'],
  reduction: ['pnd1_taxable', 'sso_base', 'pnd1_regular'],
  deduction: [],
  employer: [],
};
export type PrsAnswerKey = 'pnd1_taxable' | 'sso_base' | 'pnd1_regular';
export type PrsAccountField = 'account_id' | 'counter_account_id';
/** Which classes an item's account may be (payrollAccounts.ts ITEM_ACCOUNT_CLASSES): a deduction's credit
 *  account is a liability (a welfare fund payable) or an asset (a loan to the employee); an employer cost is
 *  booked to an expense against a liability. The FIRST class is the one "+ New account" creates — the class
 *  is taken from the target and locked (createAccountFor), never chosen in the window. */
export const PRS_ITEM_ACCOUNT_CLASSES: Record<PrsKind, Partial<Record<PrsAccountField, readonly string[]>>> = {
  earning: {},
  reduction: {},
  deduction: { account_id: ['liability', 'asset'] },
  employer: { account_id: ['expense'], counter_account_id: ['liability'] },
};
/** The usual pay day (payrollSettings.ts PAY_DAYS): '' not set, a day of the month, or the month's last day. */
export const PRS_PAY_DAYS: readonly string[] = ['', ...Array.from({ length: 31 }, (_, i) => String(i + 1)), 'eom'];
/** Every refusal the settings routes answer with (payrollEvents.ts PAYROLL_SETTINGS_ERRORS). */
export const PRS_ERRORS = ['admin_only', 'invalid_role', 'account_refused', 'account_invalid', 'code_taken', 'no_chart', 'locked',
  'firm_no_accounts', 'builtin_name', 'invalid', 'not_found', 'not_removable'] as const;
/** Where this month's social-security rate comes from (GET /settings statutory.sso.source). */
export const PRS_SSO_SOURCES = ['global', 'period_all', 'period_client'] as const;
/** What a settings change is about, and what happened to it (payroll_events.entity / .action, the ones R5 writes). */
export const PRS_EVENT_ENTITIES = ['settings', 'item_type', 'account_map'] as const;
export const PRS_EVENT_ACTIONS = ['update', 'override', 'create', 'remove'] as const;
/** The fields a settings event can name (payroll_events.field), as History says them. A field the server logs
 *  that is not listed here is shown without its old → new line, never as a raw column name. */
export const PRS_EVENT_FIELDS = ['pay_day', 'welfare_enabled', 'account_id', 'counter_account_id', 'pnd1_taxable', 'sso_base', 'pnd1_regular',
  'active', 'name_en', 'name_th', 'kind', 'summary_group'] as const;

export type PrsAccount = { id: string; code: string; name_th: string; name_en: string };
export type PrsProblem = { rule: PrsRule | string; message: string } | null;
export type PrsRoleRow = {
  role: PrsRole; classes: string[]; accountId: string; account: PrsAccount | null; problem: PrsProblem;
  setByName: string; setAt: number;
};
/** The account sections a new account takes (accountingSetup.ts CF_SECTION_VALUES / FS_SECTION_VALUES), and the
 *  classes for which the server REQUIRES a cash-flow section (cfSectionMatters) or ALLOWS a working-account
 *  section (fsSectionMatters) — read from the server, so the window asks exactly what the route will check. */
export type PrsSections = { values: string[]; requiredFor?: string[]; allowedFor?: string[] };
export type PrsPnd1Config = { expenseRate: number; expenseCap: number; ssoCap: number; brackets: { upTo: number | null; rate: number }[]; deductions: { name: string; amount: number }[] };
export type PrsPnd1Source = { kind: 'global' } | { kind: 'year'; id: number; setByName: string; setAt: number; note: string };
export type PrsSettings = {
  today: string; hasChart: boolean;
  general: { payDay: string; welfareEnabled: boolean; updatedByName: string; updatedAt: number };
  accounts: { rows: PrsRoleRow[]; answered: number; total: number };
  choices: { expense: PrsAccount[]; liability: PrsAccount[]; deduction: PrsAccount[] };
  codesInUse: string[]; cfSections: PrsSections; fsSections: PrsSections;
  items: { unanswered: number; total: number };
  statutory: {
    year: number; month: number;
    sso: { rate: number; cap: number; floor: number; source: typeof PRS_SSO_SOURCES[number] | string };
    pnd1: { config: PrsPnd1Config; source: PrsPnd1Source };
    method: string;
  };
  employerSso: {
    registered: boolean; accountNo: string; branchCode: string; registeredOn: string; form101FiledOn: string;
    current: number; inSso: number; inSsoWithoutNumber: number; notInSso: number;
  };
  ssoLoginSet: boolean; canEditFirmDefaults: boolean;
};
export type PrsScope = 'client' | 'firm';
export type PrsItem = {
  id: string; code: string; scope: PrsScope; kind: PrsKind; summaryGroup: string; builtIn: boolean; locked: boolean; active: boolean;
  nameEn: string; nameTh: string;
  pnd1Taxable: number | null; ssoBase: number | null; pnd1Regular: number | null;
  accountId: string; counterAccountId: string; account: PrsAccount | null; counterAccount: PrsAccount | null;
  accountProblem: PrsProblem; counterAccountProblem: PrsProblem;
  /** The answers still owed: answer keys and account fields, as the server names them. */
  missing: string[];
  answeredByName: string; answeredAt: number; canRemove: boolean; overridesFirm: boolean;
};
export type PrsItemRules = {
  codePattern: string; kinds: PrsKind[]; groupsByKind: Record<string, string[]>;
  answerKeysByKind: Record<string, PrsAnswerKey[]>; accountFieldsByKind: Record<string, PrsAccountField[]>;
  /** The built-in codes, and every code this client already reads (its own items and the firm's) — all pages. */
  seedCodes?: string[]; itemCodesInUse?: string[];
};
export type PrsItemPage = { rows: PrsItem[]; total: number; page: number; limit: number; unanswered: number; rules: PrsItemRules };
export type PrsHistoryRow = {
  id: number; at: number; clientId: string; entity: string; entityId: string; action: string; field: string;
  oldValue: any; newValue: any; reason: string; actorName: string; source: string;
};
/** "+ New account": for a role, or for one of an item's two account fields. */
export type PrsAccountTarget = { role: PrsRole } | { itemCode: string; field: PrsAccountField };
export type PrsNewAccount = { code: string; nameTh: string; nameEn?: string; cfSection?: string; fsSection?: string };
/** An item answer: one field per call from this screen (a single control saves when it changes). */
export type PrsItemPatch = Partial<{
  scope: PrsScope; pnd1Taxable: number | null; ssoBase: number | null; pnd1Regular: number | null;
  accountId: string; counterAccountId: string; active: boolean; nameEn: string; nameTh: string;
}>;

/** An answer key as the row and the PUT body spell it (camelCase), from the server's column name. */
export const ANSWER_PROP: Record<PrsAnswerKey, 'pnd1Taxable' | 'ssoBase' | 'pnd1Regular'> = { pnd1_taxable: 'pnd1Taxable', sso_base: 'ssoBase', pnd1_regular: 'pnd1Regular' };
export const ACCOUNT_PROP: Record<PrsAccountField, 'accountId' | 'counterAccountId'> = { account_id: 'accountId', counter_account_id: 'counterAccountId' };

const q = (clientId: string, extra: Record<string, string | number | undefined> = {}) => {
  const p = new URLSearchParams({ clientId });
  for (const [k, v] of Object.entries(extra)) if (v !== undefined && v !== '') p.set(k, String(v));
  return p.toString();
};
const E = (s: string) => encodeURIComponent(s);

export const payrollSettingsApi = {
  /** Everything the screen shows but the item list and the history. Writes nothing. */
  get: (clientId: string): Promise<PrsSettings> => apiRequest('GET', `/payroll/settings?${q(clientId)}`),
  setGeneral: (clientId: string, body: { payDay?: string; welfareEnabled?: boolean }): Promise<{ general: PrsSettings['general'] }> =>
    apiRequest('PUT', `/payroll/settings/general?${q(clientId)}`, body),
  /** accountId '' clears the answer (the row stays, account_id ''). */
  setRole: (clientId: string, role: PrsRole, accountId: string): Promise<{ accounts: PrsSettings['accounts'] }> =>
    apiRequest('PUT', `/payroll/settings/accounts?${q(clientId)}`, { role, accountId }),
  /** One chart row through accountingSetup.addAccount and the answer it was made for, in one transaction. */
  newAccount: (clientId: string, target: PrsAccountTarget, a: PrsNewAccount): Promise<{ accountId: string; accounts: PrsSettings['accounts']; codesInUse: string[] }> =>
    apiRequest('POST', `/payroll/settings/accounts/new?${q(clientId)}`, { target, ...a }),
  items: (clientId: string, scope: PrsScope, page = 1): Promise<PrsItemPage> =>
    apiRequest('GET', `/payroll/settings/item-types?${q(clientId, { scope, page })}`),
  answerItem: (clientId: string, code: string, patch: PrsItemPatch): Promise<{ row: PrsItem }> =>
    apiRequest('PUT', `/payroll/settings/item-types/${E(code)}?${q(clientId)}`, patch),
  createItem: (clientId: string, body: { code: string; kind: string; summaryGroup: string; nameEn: string; nameTh: string }): Promise<{ row: PrsItem }> =>
    apiRequest('POST', `/payroll/settings/item-types?${q(clientId)}`, body),
  /** A client's own custom item, soft-deleted; the firm row (if any) is effective again. */
  removeItem: (clientId: string, code: string): Promise<{ ok: boolean }> =>
    apiRequest('DELETE', `/payroll/settings/item-types/${E(code)}?${q(clientId)}`),
  history: (clientId: string, page = 1): Promise<{ rows: PrsHistoryRow[]; total: number; page: number; limit: number }> =>
    apiRequest('GET', `/payroll/settings/history?${q(clientId, { page })}`),
  /** (R6, section 8) The number the next payroll run of `ym` ('YYYY-MM') would take — the run numbering's dry run
   *  (GET /api/payroll/runs/next-no, routes/payrollRuns.ts). A plain read: nothing is reserved or written. */
  nextRunNo: (clientId: string, ym: string): Promise<{ docNo: string }> =>
    apiRequest('GET', `/payroll/runs/next-no?${q(clientId, { ym })}`),
};
